/**
 * Facade store for backward compatibility.
 * This re-exports state, getters, and actions from the domain-specific stores:
 * - useUserStore (user authentication and preferences)
 * - usePantryStore (pantries and pantry items)
 * - useRecipeStore (recipes and recipe books)
 * 
 * New code should import the domain-specific stores directly.
 * This facade will be removed in a future version.
 */
import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'
import type { User as FirebaseUser } from 'firebase/auth'
import { useUserStore } from './userStore'
import { usePantryStore } from './pantryStore'
import { useRecipeStore } from './recipeStore'

export const useDataStore = defineStore('data', () => {
  const userStore = useUserStore()
  const pantryStore = usePantryStore()
  const recipeStore = useRecipeStore()

  const onUserChanged = async (firebaseUser: FirebaseUser | null) => {
    const userProfile = await userStore.handleAuthChanged(firebaseUser)
    if (userProfile) {
      await recipeStore.ensurePersonalRecipeBook(userProfile)
    } else {
      // User logged out - clean up
      pantryStore.unbindAllPantryData()
      recipeStore.unbindAllRecipeData()
    }
    // Data binding is now automatic via watchers in each store
  }

  // Create a computed ref to expose userProfilePending as a placeholder for userProfileUnsubscribe
  const userProfileUnsubscribe = computed(() => null)

  return {
    // State (from userStore)
    user: computed(() => userStore.user),
    userProfileUnsubscribe,

    // State (from pantryStore)
    pantries: computed(() => pantryStore.pantries),
    pantryItemBeingEdited: computed(() => pantryStore.pantryItemBeingEdited),

    // State (from recipeStore)
    recipeBooks: computed(() => recipeStore.recipeBooks),
    searchQuery: computed(() => recipeStore.searchQuery),
    currentRecipe: computed(() => recipeStore.currentRecipe),
    discoveredRecipes: computed(() => recipeStore.discoveredRecipes),
    lastDiscoveredRecipe: computed(() => recipeStore.lastDiscoveredRecipe),
    moreToDiscover: computed(() => recipeStore.moreToDiscover),

    // User Getters
    getUser: computed(() => userStore.getUser),
    colors: computed(() => userStore.colors),
    isLoggedIn: computed(() => userStore.isLoggedIn),
    currentPantry: computed(() => userStore.currentPantry),
    pantryItemOptionalDataPanel: computed(() => userStore.pantryItemOptionalDataPanel),
    pantryItemFormComplexityDefault: computed(() => userStore.pantryItemFormComplexityDefault),
    showPantryItemNotes: computed(() => userStore.showPantryItemNotes),
    showPantryItemExpirationDate: computed(() => userStore.showPantryItemExpirationDate),
    showPantryItemDateAdded: computed(() => userStore.showPantryItemDateAdded),
    showPantryItemPrice: computed(() => userStore.showPantryItemPrice),
    showPantryItemTotalCalories: computed(() => userStore.showPantryItemTotalCalories),
    showPantryItemBrand: computed(() => userStore.showPantryItemBrand),
    showPantryItemLocation: computed(() => userStore.showPantryItemLocation),

    // Pantry Getters
    getPantries: computed(() => pantryStore.getPantries),
    getPantriesDocs: computed(() => pantryStore.getPantriesDocs),
    tasks: computed(() => pantryStore.tasks),
    getPantry: pantryStore.getPantry,
    getPantryMembers: pantryStore.getPantryMembers,
    areMultiplePantries: computed(() => pantryStore.areMultiplePantries),
    pantryPantryItems: pantryStore.pantryPantryItems,
    getPantryPantryItems: pantryStore.getPantryPantryItems,
    getPantryItemBeingEdited: computed(() => pantryStore.getPantryItemBeingEdited),
    getUserShoppingList: computed(() => pantryStore.getUserShoppingList),
    getPantryShoppingList: pantryStore.getPantryShoppingList,
    getIngredientIsInCurrentPantry: pantryStore.getIngredientIsInCurrentPantry,
    ingredientIsInCurrentPantry: pantryStore.ingredientIsInCurrentPantry,

    // Recipe Getters
    getRecipeBooks: computed(() => recipeStore.getRecipeBooks),
    getRecipeBooksDocs: computed(() => recipeStore.getRecipeBooksDocs),
    getSearchQuery: computed(() => recipeStore.getSearchQuery),
    getCurrentRecipe: computed(() => recipeStore.getCurrentRecipe),
    getDiscoveredRecipes: computed(() => recipeStore.getDiscoveredRecipes),
    discoveredRecipe: computed(() => recipeStore.discoveredRecipe),
    getLastDiscoveredRecipe: computed(() => recipeStore.getLastDiscoveredRecipe),
    getMoreToDiscover: computed(() => recipeStore.getMoreToDiscover),
    recipeFromRecipeBook: recipeStore.recipeFromRecipeBook,
    recipeBooksContainingRecipe: recipeStore.recipeBooksContainingRecipe,
    recipeBook: recipeStore.recipeBook,
    getRecipeBookMembers: recipeStore.getRecipeBookMembers,
    recipeBookMembers: recipeStore.recipeBookMembers,
    recipeBookRecipesFiltered: recipeStore.recipeBookRecipesFiltered,
    userAddedShoppingListItems: computed(() => recipeStore.userAddedShoppingListItems),
    loading: computed(() => recipeStore.loading),
    getIngredientsSmartSuggestions: recipeStore.getIngredientsSmartSuggestions,

    // User Actions
    setUser: () => { }, // No-op, handled internally by userStore
    bindUserProfile: () => Promise.resolve(), // No-op, VueFire handles this
    setCurrentPantry: userStore.setCurrentPantry,
    saveColor: userStore.saveColor,
    savePantryItemOptionalDataPanel: userStore.savePantryItemOptionalDataPanel,
    unbindProfile: userStore.unbindProfile,

    // Pantry Actions
    setPantryItemBeingEdited: pantryStore.setPantryItemBeingEdited,
    clearPantryItemBeingEdited: pantryStore.clearPantryItemBeingEdited,
    bindPantryMembers: pantryStore.bindPantryMembers,
    bindPantryPantryItems: pantryStore.bindPantryItems,
    bindPantryItems: pantryStore.bindPantryItems,
    addUserToPantry: pantryStore.addUserToPantry,
    upsertPantry: pantryStore.upsertPantry,
    addItemToUserShoppingList: pantryStore.addItemToUserShoppingList,
    removeItemsFromUserShoppingList: pantryStore.removeItemsFromUserShoppingList,
    unbindAllPantryData: pantryStore.unbindAllPantryData,
    removePantryItemFromPantry: pantryStore.removePantryItemFromPantry,
    setPantryItemStatusToInStock: pantryStore.setPantryItemStatusToInStock,
    updatePantryItemQuantity: pantryStore.updatePantryItemQuantity,
    setPantryItemStatusToOutOfStock: pantryStore.setPantryItemStatusToOutOfStock,
    addItemToPantry: pantryStore.addItemToPantry,
    addItemToCurrentPantry: pantryStore.addItemToCurrentPantry,
    cyclePantryItemStatus: pantryStore.cyclePantryItemStatus,
    savePantryItemBeingEdited: pantryStore.savePantryItemBeingEdited,
    addIngredientAsPantryItemAlias: pantryStore.addIngredientAsPantryItemAlias,
    mergePantryItems: pantryStore.mergePantryItems,
    removeUserFromPantry: pantryStore.removeUserFromPantry,
    editPantryItem: pantryStore.editPantryItem,
    pantry: pantryStore.pantry,

    // Recipe Actions
    setSearchQuery: recipeStore.setSearchQuery,
    setCurrentRecipe: recipeStore.setCurrentRecipe,
    clearDiscoveredRecipes: recipeStore.clearDiscoveredRecipes,
    addDiscoveredRecipe: recipeStore.addDiscoveredRecipe,
    setLastDiscoveredRecipe: recipeStore.setLastDiscoveredRecipe,
    setMoreToDiscover: recipeStore.setMoreToDiscover,
    bindCurrentRecipe: recipeStore.bindCurrentRecipe,
    bindRecipeBookMembers: recipeStore.bindRecipeBookMembers,
    bindRecipeBookRecipes: recipeStore.bindRecipeBookRecipes,
    bindAllRecipeBookRecipes: recipeStore.bindAllRecipeBookRecipes,
    addUserToRecipeBook: recipeStore.addUserToRecipeBook,
    removeUserFromRecipeBook: recipeStore.removeUserFromRecipeBook,
    discoverMoreRecipes: recipeStore.discoverMoreRecipes,
    saveRecipe: recipeStore.saveRecipe,
    unbindAllRecipeData: recipeStore.unbindAllRecipeData,
    upsertRecipeBook: recipeStore.upsertRecipeBook,
    addRecipeToRecipeBook: recipeStore.addRecipeToRecipeBook,
    addExistingRecipeToRecipeBook: recipeStore.addExistingRecipeToRecipeBook,
    removeRecipeFromRecipeBook: recipeStore.removeRecipeFromRecipeBook,
    deleteRecipeCompletely: recipeStore.deleteRecipeCompletely,
    cloneRecipe: recipeStore.cloneRecipe,
    getOriginalRecipe: recipeStore.getOriginalRecipe,
    updateRecipeFields: recipeStore.updateRecipeFields,
    testFirebaseConnection: recipeStore.testFirebaseConnection,

    // Combined
    onUserChanged
  }
})
